import { useQueryClient } from "@tanstack/react-query";
import React from "react";

import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { AnswerTypeForm as EditComponent } from "./answerTypeForm";

import { AnswerTypeView as AnswerTypeViewComponent } from "./answerTypeView";
import { AnswerTypePage } from "./answerTypePage";
import { AnswerTypeService } from "./answerTypeService";
import { LookupAnswerTypeDto } from "../../../services/dto";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

export const AnswerTypeContainer = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [data, setData] = React.useState<LookupAnswerTypeDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const answertypeQuery = AnswerTypeService.getAnswerTypeQuery(
    data?.id ?? undefined
  );

  const { status: saveStatus, mutateAsync: saveAnswertype } = AnswerTypeService.save();
  const { status: deActivateStatus, mutate: deleteMutate } =
    AnswerTypeService.deActivateQuestiontype();
  const { setSearchAction, clearSearchAction } = useActionFromQuery(OnCustomEvent);
  function EditRecord(data: any, showView: boolean | undefined) {
    setData(data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, data?.id);
  }

  function OnCustomEvent(actionType: string, dataId: string) {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as LookupAnswerTypeDto);
      setSearchAction(actionType, dataId);
    }
  }

  const handleSubmit = async (values: LookupAnswerTypeDto) => {
    const isEdit = data && data.id && data.id !== Constants.emptyGuid;
    values.id = isEdit ? data.id : Constants.emptyGuid;
    const message = isEdit ? "Answer type updated" : "Answer type saved";
    return await saveAnswertype(values, {
      onSuccess: (response) => {
        const errorMessage = apiErrorMessage(response.errorMessages);
        if (errorMessage) {
          showSnackbar(errorMessage, MessageSeverity.Error);
        } else {
          showSnackbar(message, MessageSeverity.Success);
          answertypeQuery.refetch();
          queryClient.invalidateQueries({ queryKey: ["answertypeGridQuery"] });
          closeDrawer();
        }
      },
    });
  };

  const closeDrawer = () =>{
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const handleDelete = () => {
    deleteMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => {
        const errorMessage = apiErrorMessage(response.errorMessages);
        if (errorMessage) {
          showSnackbar(errorMessage, MessageSeverity.Error);
        } else {
          showSnackbar("Answertype deleted", MessageSeverity.Success);
          answertypeQuery.refetch();
          queryClient.invalidateQueries({ queryKey: ["answertypeGridQuery"] });
          closeDrawer();
        }
      },
    });
  };

  const renderDrawerContent = () => {
    const isDataLoaded = !!answertypeQuery?.data;
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW: {
        return (
          <EditComponent
            show={true}
            onHide={() => closeDrawer()}
            onSave={(data: LookupAnswerTypeDto) => handleSubmit(data)}
          />
        );
      }

      case GRID_ACTION_TYPES.EDIT: {
        return isDataLoaded ? (
          <EditComponent
            data={answertypeQuery?.data}
            show={!!answertypeQuery?.data}
            onHide={() => closeDrawer()}
            onSave={(data: LookupAnswerTypeDto) => handleSubmit(data)}
          />
        ) : null;
      }
      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK: {
        return isDataLoaded ? (
          <AnswerTypeViewComponent
            data={answertypeQuery?.data}
            show={!!answertypeQuery?.data}
            onHide={() => closeDrawer()}
            onDelete={handleDelete}
            onEdit={() => EditRecord(answertypeQuery.data, true)}
          />
        ) : null;
      }
    }
  };

  return (
    <div>
      <AnswerTypePage onCustomEvent={OnCustomEvent} />
      {renderDrawerContent()}
    </div>
  );
};
